import { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import { Button, Chip } from '@structural-climbing/ui'
import { SurfaceCard } from '../../design/SurfaceCard'
import { useMyProfile } from '../profile/useMyProfile'
import { safeReturnTo } from '../profile/profileFixLink'
import { EmailCodeForm } from './EmailCodeForm'

/**
 * The profile's email section: the address on the account, whether the member
 * has proved it is theirs, and the way to prove it.
 *
 * The address itself is not editable here. It is the one the member signs in
 * with, and it is proved by typing a code sent to it, never by a profile edit.
 *
 * Arriving from the banner (`?field=email&returnTo=…`) opens the code step
 * straight away and sends the member back where they were once it is done.
 */
export function EmailVerificationCard() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { profile, refresh } = useMyProfile()
  const arrivedToVerify = searchParams.get('field') === 'email'
  const returnTo = arrivedToVerify ? safeReturnTo(searchParams.get('returnTo')) : null

  const [verifying, setVerifying] = useState(false)
  // Shown as verified the moment the code is accepted, without waiting for the
  // profile to be read again.
  const [justVerified, setJustVerified] = useState(false)
  const cardRef = useRef<HTMLDivElement>(null)

  const email = profile?.email ?? null
  const verified = justVerified || profile?.emailVerified === true

  const opened = useRef(false)
  useEffect(() => {
    if (!arrivedToVerify || opened.current || !email || profile?.emailVerified !== false) return
    opened.current = true
    setVerifying(true)
    cardRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'center' })
  }, [arrivedToVerify, email, profile?.emailVerified])

  // Nothing to show until the Worker says what the address is. An older Worker
  // sends no `email` at all, and then this section simply is not there.
  if (!email) return null

  return (
    <div ref={cardRef}>
      <SurfaceCard className="flex flex-col gap-3 p-6">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="min-w-0">
            <p className="text-sm font-medium">{t('emailCode.profileTitle')}</p>
            <p className="truncate text-sm text-foreground/70">{email}</p>
          </div>
          <Chip size="sm" variant="flat" color={verified ? 'success' : 'warning'}>
            {verified ? t('emailCode.verified') : t('emailCode.notVerified')}
          </Chip>
        </div>

        {!verified && !verifying && (
          <>
            <p className="text-xs text-foreground/50">{t('emailCode.profileHint')}</p>
            <Button color="primary" variant="flat" onClick={() => setVerifying(true)}>
              {t('emailCode.verifyCta')}
            </Button>
          </>
        )}

        {!verified && verifying && (
          <EmailCodeForm
            email={email}
            onVerified={() => {
              setJustVerified(true)
              setVerifying(false)
              refresh()
              if (returnTo) navigate(returnTo)
            }}
          />
        )}
      </SurfaceCard>
    </div>
  )
}
